
import MediaHelper from '../helpers/MediaHelper';
import {safeStringSplit, isTrueString, toggleSlide, toggleDisplay, arrayUnique} from './Util';

// Utility function to help with dispatching the custom event.
const dispatchSameNameEventOn = ( targetName ) => {
	// Dispatch Event for all Input Elements with the same name.
	document.querySelectorAll( 'input' ).forEach( inputEl => {
		if ( inputEl.name === targetName ) {
			// Fire Custom Event to trigger the change for all sections.
			inputEl.dispatchEvent( new CustomEvent( 'change.same_name' ) );
		}
	} )
};

class TriggerFormSection {

	constructor( triggerEl ) {

		this.triggerEl = triggerEl;
		this.inputEls = this.triggerEl.dataset.formSectionName ?
			Array.from( this.triggerEl.querySelectorAll( 'input, select, textarea' ) ).filter( inputEl => inputEl.name === this.triggerEl.dataset.formSectionName ) :
			[ this.triggerEl ];

		// Get Method and Targets.
		this.methods = safeStringSplit( triggerEl.dataset.formSection );
		this.targets = safeStringSplit( triggerEl.dataset.formSectionTarget );

		// Trigger Change event on all inputs.
		this.inputEls.forEach( inputEl => {
			inputEl.addEventListener( 'change.same_name', () => this.toggle() );
		} );
		this.toggle( false );
	}

	isSelected() {
		let inputEls = this.inputEls.filter( inputEl => inputEl.checked );
		const targetValue = this.triggerEl.dataset.formSectionValue
		if ( targetValue ) {
			inputEls = inputEls.filter( inputEl => inputEl.value === targetValue );
		}

		return inputEls.length > 0;
	}

	toggle( withAnimation = true ) {

		const triggerEl  = this.triggerEl;
		const isSelected = this.isSelected();

		this.targets.forEach( ( targetName, index ) => {

			// Get method for the current target.
			const method = this.methods[ index ] ?? 'show';

			// Skip if no method.
			if ( 'none' === method ) {
				return;
			}

			// Get Section Element by ID.
			const sectionEl = '#' === targetName.at( 0 ) ? document.querySelector( targetName ) : document.getElementById( 'form_section_' + targetName );

			// Prevent errors, if no section was found.
			if ( ! sectionEl ) {
				console.error( "FormSection Target with id: form_section_" + targetName + " not found!" );
				return;
			}

			// These Elements will NOT be disabled, when the section is getting hidden.
			const excludedInputEls = Array.from( sectionEl.querySelectorAll( '.form_section_target' ) ).reduce( ( elements, containerEl ) => {
				return elements.concat( Array.from( containerEl.querySelectorAll( 'input, textarea, select' ) ) );
			}, [] );

			// Get all inputs, except the ones we use to toggle the form itself.
			const inputEls = Array.from( sectionEl.querySelectorAll( 'input, textarea, select' ) ).filter( inputEl => ! excludedInputEls.includes( inputEl ) );

			// Whether to show or hide the current section.
			const toggleState = ( isSelected && method === 'show' ) || ( ! isSelected && method === 'hide' );

			// Set the Section Element and the Inputs active/inactive.
			triggerEl.classList.toggle( 'active', toggleState );
			sectionEl.classList.toggle( 'active', toggleState );
			inputEls.forEach( inputEl => {
				inputEl.disabled = ! toggleState;
			} );

			// Edge-Case:
			// If we have disabled a form-section element nested inside, but other elements with the same name stay valid, we handle that case.
			if ( ! toggleState ) {
				inputEls.forEach( inputEl => {

					// Do not trigger for the current name - this prevents infinite loops.
					if ( inputEl.name === this.triggerEl.name ) {
						return;
					}

					// Nested form section - We disabled this element, but another will take its place.
					if ( inputEl.matches( '[data-form-section-nested-fallback]' ) && inputEl.checked ) {
						const fallbackEl = document.getElementById( inputEl.dataset.formSectionNestedFallback );
						if ( fallbackEl !== inputEl ) {

							fallbackEl.checked = true;
							dispatchSameNameEventOn( fallbackEl.name );
						}
					}
				} );
			}

			// Check whether we should
			const doAnimation = withAnimation && isTrueString( sectionEl.dataset.formSectionAnimation ?? 'true' );
			const doScrollTop = withAnimation && isTrueString( triggerEl.dataset.formSectionScroll ?? 'true' );

			// Show the section.
			if ( doAnimation ) {
				toggleSlide( sectionEl, toggleState );
			} else {
				toggleDisplay( sectionEl, toggleState );
			}

			// Scroll to section on small screens, whenever we open them.
			if ( toggleState && doScrollTop ) {
				if ( MediaHelper.matches( null, 'desktop' ) ) {
					setTimeout( () => {
						const offsetTop = Math.ceil( sectionEl.offsetTop - 44 - 50 ); // 44 for header, 50 for form section headline
						window.scrollTo( { top: offsetTop, behavior: 'smooth' } );
					}, 1 );
				}
			}
		} );
	}
}

// Only initialise the global listeners once.
let isInitialized = false;

const initTriggerFormSection = function( scopeEl = document ) {

	// Initialize FormSection Trigger.
	scopeEl.querySelectorAll( '[data-form-section]' ).forEach( formSectionEl => {
		// Only init once per element - even if the function is called multiple times.
		formSectionEl.EN_TriggerFormSection ??= new TriggerFormSection( formSectionEl );
	} );

	// Only add global listener once.
	if ( isInitialized ) {
		return;
	}
	isInitialized = true;

	// Delegate to catch change events for all form section inputs.
	document.addEventListener( 'change', ( ev ) => {

		// Catch change events for all form section inputs.
		if ( ev.target && 'INPUT' === ev.target.tagName && ev.target.closest( '[data-form-section]' ) ) {
			const targetName = ev.target.getAttribute( 'name' );
			if ( targetName ) {
				dispatchSameNameEventOn( targetName );
			}
		}
	} );

	document.addEventListener( 'reset', ( ev ) => {

		if ( ev.target && ev.target.querySelector( '[data-form-section]' ) ) {

			// Get all Input Names of the resettet form.
			const inputEls = Array.from( ev.target.querySelectorAll( '[data-form-section]:is(input, select, textarea), [data-form-section] :is(input, select, textarea)' ) );

			// Get unique names of all input elements that may have changed by the input.
			const targetNames = arrayUnique( inputEls.map( inputEl => inputEl.getAttribute( 'name' ) ) );

			// Trigger change event on all input elements with the same name.
			// Timeout is because reset still uses the old values.
			setTimeout( () => {
				targetNames.forEach( targetName => dispatchSameNameEventOn( targetName ) );
			}, 0 );
		}
	} );
};

export {
	TriggerFormSection as default,
	initTriggerFormSection
}
